import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';
import EnhancedProductForm from './EnhancedProductForm';
import ManualOrderForm from './ManualOrderForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to construct image URLs (GridFS-compatible)
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath;
  
  // If path already starts with /api/images/ (GridFS), use it as-is
  if (imagePath.startsWith('/api/images/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${imagePath}`;
  }
  
  // If path already starts with /api/files/, use it as-is
  if (imagePath.startsWith('/api/files/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${imagePath}`;
  }
  
  // Remove leading slash
  let cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
  
  // Handle different path formats
  if (cleanPath.startsWith('api/images/')) {
    // GridFS path, use as-is
    return `${process.env.REACT_APP_BACKEND_URL}/${cleanPath}`;
  } else if (cleanPath.startsWith('api/files/')) {
    // Old file path, use as-is
    return `${process.env.REACT_APP_BACKEND_URL}/${cleanPath}`;
  } else if (cleanPath.startsWith('api/uploads/')) {
    // Old upload path, extract filename and use GridFS
    const filename = cleanPath.substring(12);
    return `${process.env.REACT_APP_BACKEND_URL}/api/images/${filename}`;
  } else if (cleanPath.startsWith('uploads/')) {
    // Old upload path, extract filename and use GridFS
    const filename = cleanPath.substring(8);
    return `${process.env.REACT_APP_BACKEND_URL}/api/images/${filename}`;
  }
  
  // Assume it's just a filename, use GridFS
  return `${process.env.REACT_APP_BACKEND_URL}/api/images/${cleanPath}`;
};

// Products Management Component
export const ProductsManagement = () => {
  const theme = useThemeClasses();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [flatRates, setFlatRates] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [formMode, setFormMode] = useState('tabbed'); // 'wizard', 'tabbed', or 'fullpage'
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    images: [],
    base_options: {},  // For options with/without pricing
    custom_options: {}, // For text inputs with character limits
    addons: [],  // For addon products with preview images
    featured: false,  // For featured products on homepage
    file_upload_enabled: true,  // Whether customers can upload files for this product
    file_upload_required: false,  // Whether file upload is mandatory
    max_files_allowed: 10,
    allowed_file_types: 'image/*,.pdf,.stl,.obj,.zip,.rar',
    max_file_size_mb: 10,
    // Costing Model
    cost_breakdown: {
      materials: [],
      total_material_cost: 0,
      labor_cost: 0,
      overhead_cost: 0,
      total_cost: 0,
      profit_margin_percent: 0,
      suggested_price: 0
    },
    // Inventory Management
    track_inventory: false,
    stock_quantity: 0,
    low_stock_threshold: 5,
    is_made_to_order: false,
    // Shipping Information (admin only)
    weight: 0.0,
    weight_unit: 'lb',
    length: 0.0,
    width: 0.0,
    height: 0.0,
    dimension_unit: 'inch',
    package_type: 'package',
    // Enhanced Canada Post Requirements
    declared_value: 0.0,
    fragile_item: false,
    signature_required: false,
    delivery_confirmation: false,
    // Prepaid Shipping Options
    prepaid_shipping_enabled: false,
    selected_flat_rate_id: null
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingAddonImages, setUploadingAddonImages] = useState({});
  const [showCopyFrom, setShowCopyFrom] = useState(false);
  const [selectedProductToCopy, setSelectedProductToCopy] = useState('');
  const [materials, setMaterials] = useState([]);
  const [showCostingModel, setShowCostingModel] = useState(false);

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadFlatRates();
    loadMaterials();
  }, []);


  useEffect(() => {
    // Check if we should auto-edit a product
    const editProductId = sessionStorage.getItem('editProductId');
    if (editProductId && products.length > 0) {
      const productToEdit = products.find(p => p.id === editProductId);
      if (productToEdit) {
        handleEdit(productToEdit);
        sessionStorage.removeItem('editProductId');
      }
    }
  }, [products]);


  const loadFlatRates = async () => {
    try {
      const response = await axios.get(`${API}/admin/flat-rates`);
      setFlatRates(response.data);
    } catch (error) {
      console.error('Failed to load flat rates:', error);
      // Don't show error toast as this is optional functionality
    }
  };


  const loadMaterials = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterials(response.data);
    } catch (error) {
      console.error('Failed to load materials:', error);
      // Don't show error toast as this is optional
    }
  };


  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    }
  };

  const handleCopyFromProduct = (productId) => {
    const productToCopy = products.find(p => p.id === productId);
    if (productToCopy) {
      // Copy all fields except id and images (keep empty for new upload)
      setFormData({
        name: `${productToCopy.name} (Copy)`,
        description: productToCopy.description || '',
        price: productToCopy.price || '',
        category: productToCopy.category || '',
        images: [], // Don't copy images, let user upload new ones
        base_options: productToCopy.base_options || {},
        custom_options: productToCopy.custom_options || {},
        addons: productToCopy.addons || [],
        featured: productToCopy.featured || false,
        file_upload_enabled: productToCopy.file_upload_enabled !== undefined ? productToCopy.file_upload_enabled : true,
        file_upload_required: productToCopy.file_upload_required || false,
        max_files_allowed: productToCopy.max_files_allowed || 10,
        allowed_file_types: productToCopy.allowed_file_types || 'image/*,.pdf,.stl,.obj,.zip,.rar',
        max_file_size_mb: productToCopy.max_file_size_mb || 10,
        cost_breakdown: productToCopy.cost_breakdown || { materials: [], total_material_cost: 0, labor_cost: 0, overhead_cost: 0, total_cost: 0, profit_margin_percent: 0, suggested_price: 0 },
        track_inventory: productToCopy.track_inventory || false,
        stock_quantity: productToCopy.stock_quantity || 0,
        low_stock_threshold: productToCopy.low_stock_threshold || 5,
        is_made_to_order: productToCopy.is_made_to_order || false,
        weight: productToCopy.weight || 0.0,
        weight_unit: productToCopy.weight_unit || 'lb',
        length: productToCopy.length || 0.0,
        width: productToCopy.width || 0.0,
        height: productToCopy.height || 0.0,
        dimension_unit: productToCopy.dimension_unit || 'inch',
        package_type: productToCopy.package_type || 'package',
        declared_value: productToCopy.declared_value || 0.0,
        fragile_item: productToCopy.fragile_item || false,
        signature_required: productToCopy.signature_required || false,
        delivery_confirmation: productToCopy.delivery_confirmation || false,
        prepaid_shipping_enabled: productToCopy.prepaid_shipping_enabled || false,
        selected_flat_rate_id: productToCopy.selected_flat_rate_id || null
      });
      toast.success(`Copied data from "${productToCopy.name}". You can now modify and save as a new product.`);
      setShowCopyFrom(false);
      setSelectedProductToCopy('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price)
      };

      // Transform cost_breakdown materials to backend format
      if (productData.cost_breakdown && productData.cost_breakdown.materials) {
        productData.cost_breakdown.materials = productData.cost_breakdown.materials.map(mat => {
          const materialData = materials?.find(m => m.id === mat.material_id);
          return {
            material_id: mat.material_id,
            material_name: materialData?.name || 'Unknown Material',
            quantity_used: mat.is_weight_based ? (mat.weight_used || 0) : (mat.quantity || 0),
            unit_of_measure: mat.is_weight_based ? (mat.weight_unit || 'g') : (mat.unit || 'pieces'),
            cost_per_unit: mat.cost_per_unit || 0,
            total_cost: mat.total_cost || 0,
            weight_unit_selected: mat.is_weight_based ? (mat.weight_unit || 'g') : null
          };
        });
      }

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, productData);
        toast.success('Product added successfully');
      }

      setFormData({ name: '', description: '', price: '', category: '', images: [], base_options: {}, custom_options: {}, addons: [], featured: false, file_upload_enabled: true, file_upload_required: false, max_files_allowed: 10, allowed_file_types: 'image/*,.pdf,.stl,.obj,.zip,.rar', max_file_size_mb: 10, cost_breakdown: { materials: [], total_material_cost: 0, labor_cost: 0, overhead_cost: 0, total_cost: 0, profit_margin_percent: 0, suggested_price: 0 }, track_inventory: false, stock_quantity: 0, low_stock_threshold: 5, is_made_to_order: false, weight: 0.0, weight_unit: 'lb', length: 0.0, width: 0.0, height: 0.0, dimension_unit: 'inch', package_type: 'package', declared_value: 0.0, fragile_item: false, signature_required: false, delivery_confirmation: false, prepaid_shipping_enabled: false, selected_flat_rate_id: null });
      setShowAddForm(false);
      setEditingProduct(null);
      setShowCopyFrom(false);
      setSelectedProductToCopy('');
      loadProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    // Set the product to edit
    setEditingProduct(product);
    
    // Transform cost_breakdown materials from backend to frontend format
    let transformedCostBreakdown = { materials: [], total_material_cost: 0, labor_cost: 0, overhead_cost: 0, total_cost: 0, profit_margin_percent: 0, suggested_price: 0 };
    
    if (product.cost_breakdown) {
      transformedCostBreakdown = {
        ...product.cost_breakdown,
        materials: (product.cost_breakdown.materials || []).map(mat => {
          // Backend stores quantity_used for both types
          // If weight_unit_selected exists, it's weight-based
          const isWeightBased = !!mat.weight_unit_selected;
          
          return {
            material_id: mat.material_id,
            material_name: mat.material_name,
            is_weight_based: isWeightBased,
            // Transform quantity_used back to weight_used or quantity
            weight_used: isWeightBased ? mat.quantity_used : 0,
            weight_unit: mat.weight_unit_selected || 'g',
            quantity: !isWeightBased ? mat.quantity_used : 0,
            unit: mat.unit_of_measure || 'pieces',
            cost_per_unit: mat.cost_per_unit,
            total_cost: mat.total_cost
          };
        })
      };
    }
    
    // Pre-populate form data
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      images: product.images || [],
      base_options: product.base_options || {},
      custom_options: product.custom_options || {},
      addons: product.addons || [],
      featured: product.featured || false,
      file_upload_enabled: product.file_upload_enabled !== false,  // Default to true if not set
      file_upload_required: product.file_upload_required || false,
      max_files_allowed: product.max_files_allowed || 10,
      allowed_file_types: product.allowed_file_types || 'image/*,.pdf,.stl,.obj,.zip,.rar',
      max_file_size_mb: product.max_file_size_mb || 10,
      // Costing Model - with transformed materials
      cost_breakdown: transformedCostBreakdown,
      // Inventory Management
      track_inventory: product.track_inventory || false,
      stock_quantity: product.stock_quantity || 0,
      low_stock_threshold: product.low_stock_threshold || 5,
      is_made_to_order: product.is_made_to_order || false,
      // Shipping Information
      weight: product.weight || 0.0,
      weight_unit: product.weight_unit || 'lb',
      length: product.length || 0.0,
      width: product.width || 0.0,
      height: product.height || 0.0,
      dimension_unit: product.dimension_unit || 'inch',
      package_type: product.package_type || 'package',
      // Enhanced Canada Post Requirements
      declared_value: product.declared_value || 0.0,
      fragile_item: product.fragile_item || false,
      signature_required: product.signature_required || false,
      delivery_confirmation: product.delivery_confirmation || false,
      // Prepaid Shipping Options
      prepaid_shipping_enabled: product.prepaid_shipping_enabled || false,
      selected_flat_rate_id: product.selected_flat_rate_id || null
    });
    
    // Show mode selector instead of directly opening form
    setShowModeSelector(true);
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingImages(true);
    const successfulUploads = [];
    const failedUploads = [];
    
    try {
      // Upload files one by one to better handle errors
      for (const file of Array.from(files)) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          
          // Use dedicated product image upload endpoint
          const response = await axios.post('/api/upload/product-image', formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          if (response.data && response.data.upload_path) {
            successfulUploads.push(response.data.upload_path);
          } else {
            failedUploads.push(file.name);
          }
        } catch (fileError) {
          console.error(`Failed to upload ${file.name}:`, fileError);
          failedUploads.push(file.name);
        }
      }
      
      // Only add successfully uploaded images
      if (successfulUploads.length > 0) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...successfulUploads]
        }));
        toast.success(`Successfully uploaded ${successfulUploads.length} image(s)`);
      }
      
      // Show errors for failed uploads
      if (failedUploads.length > 0) {
        toast.error(`Failed to upload ${failedUploads.length} image(s): ${failedUploads.join(', ')}`);
      }
    } catch (error) {
      console.error('Failed to upload images:', error);
      toast.error('Failed to upload images: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleAddonImageUpload = async (formDataOrIndex, files) => {
    // Handle new format (FormData object passed directly)
    if (formDataOrIndex instanceof FormData) {
      try {
        // Use dedicated product image upload endpoint for addons too
        const response = await axios.post(`${API}/upload/product-image`, formDataOrIndex, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data; // Return the response for the caller to handle
      } catch (error) {
        console.error('Failed to upload addon image:', error);
        toast.error('Failed to upload addon image');
        throw error;
      }
    }
    
    // Handle old format (addonIndex + files)
    const addonIndex = formDataOrIndex;
    if (!files || files.length === 0) return;
    
    setUploadingAddonImages(prev => ({ ...prev, [addonIndex]: true }));
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        // Use dedicated product image upload endpoint
        const response = await axios.post(`${API}/upload/product-image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        return response.data.file_url || response.data.image_url;
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      const newAddons = [...formData.addons];
      newAddons[addonIndex] = {
        ...newAddons[addonIndex],
        preview_images: [...(newAddons[addonIndex].preview_images || []), ...uploadedUrls]
      };
      setFormData(prev => ({ ...prev, addons: newAddons }));
      
      toast.success(`Successfully uploaded ${uploadedUrls.length} addon image(s)`);
    } catch (error) {
      console.error('Failed to upload addon images:', error);
      toast.error('Failed to upload addon images');
    } finally {
      setUploadingAddonImages(prev => ({ ...prev, [addonIndex]: false }));
    }
  };

  const handleRemoveAddonImage = (addonIndex, imageIndex) => {
    const newAddons = [...formData.addons];
    newAddons[addonIndex] = {
      ...newAddons[addonIndex],
      preview_images: newAddons[addonIndex].preview_images.filter((_, index) => index !== imageIndex)
    };
    setFormData(prev => ({ ...prev, addons: newAddons }));
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Product deleted successfully');
      loadProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Products Management</h2>
        <div className="flex gap-3">
          {/* Copy Product Button */}
          <div className="relative">
            <button
              onClick={() => setShowCopyFrom(!showCopyFrom)}
              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 flex items-center gap-2 transition-colors"
            >
              <span>📋</span>
              {showCopyFrom ? 'Cancel Copy' : 'Copy Product'}
            </button>
            
            {/* Copy Product Dropdown */}
            {showCopyFrom && (
              <div className={`absolute right-0 mt-2 w-96 ${theme.card} rounded-lg shadow-xl border ${theme.border} p-4 z-50`}>
                <h4 className={`font-semibold ${theme.textPrimary} mb-2`}>Copy from Existing Product</h4>
                <p className={`text-sm ${theme.textSecondary} mb-3`}>
                  Select a product to duplicate. All details except images will be copied.
                </p>
                <select
                  value={selectedProductToCopy}
                  onChange={(e) => setSelectedProductToCopy(e.target.value)}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 mb-3`}
                >
                  <option value="">-- Select a product --</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} - ${product.price} {product.category ? `(${product.category})` : ''}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    if (selectedProductToCopy) {
                      handleCopyFromProduct(selectedProductToCopy);
                      setShowAddForm(true);
                    }
                  }}
                  disabled={!selectedProductToCopy}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  Copy & Edit Product
                </button>
              </div>
            )}
          </div>
          
          {/* Add Product Button */}
          <button
            onClick={() => {
              if (showAddForm) {
                setShowAddForm(false);
                setEditingProduct(null);
                setShowCopyFrom(false);
              } else {
                setShowModeSelector(true);
              }
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            {showAddForm ? (
              <>
                <span>✕</span>
                <span>Cancel</span>
              </>
            ) : (
              <>
                <span>➕</span>
                <span>Add Product</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Mode Selector Modal */}
      {showModeSelector && !showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className={`text-2xl font-bold ${theme.textPrimary}`}>
                    {editingProduct ? 'Choose How to Edit Product' : 'Choose How to Add Product'}
                  </h3>
                  <p className={`text-sm ${theme.textSecondary} mt-1`}>
                    {editingProduct ? `Editing: ${editingProduct.name}` : 'Select the method that works best for you'}
                  </p>
                </div>
                <button
                  onClick={() => setShowModeSelector(false)}
                  className={`${theme.bgSecondary} hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-2 transition-colors`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Mode Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Wizard Mode */}
                <button
                  onClick={() => {
                    setShowModeSelector(false);
                    setShowAddForm(true);
                    setFormMode('wizard');
                    // Don't reset formData or editingProduct - keep them if editing
                    if (!editingProduct) {
                      setShowCopyFrom(false);
                      setFormData({ name: '', description: '', price: '', category: '', images: [], base_options: {}, custom_options: {}, addons: [], featured: false, file_upload_enabled: true, file_upload_required: false, max_files_allowed: 10, allowed_file_types: 'image/*,.pdf,.stl,.obj,.zip,.rar', max_file_size_mb: 10, cost_breakdown: { materials: [], total_material_cost: 0, labor_cost: 0, overhead_cost: 0, total_cost: 0, profit_margin_percent: 0, suggested_price: 0 }, track_inventory: false, stock_quantity: 0, low_stock_threshold: 5, is_made_to_order: false, weight: 0.0, weight_unit: 'lb', length: 0.0, width: 0.0, height: 0.0, dimension_unit: 'inch', package_type: 'package', declared_value: 0.0, fragile_item: false, signature_required: false, delivery_confirmation: false, prepaid_shipping_enabled: false, selected_flat_rate_id: null });
                    }
                  }}
                  className={`text-left p-6 rounded-xl border-2 border-purple-500 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 hover:shadow-lg transition-all`}
                >
                  <div className="flex flex-col gap-3">
                    <div className="text-4xl">🧭</div>
                    <div>
                      <h4 className={`text-lg font-bold ${theme.textPrimary} mb-2`}>Wizard Mode</h4>
                      <p className={`text-sm ${theme.textSecondary} mb-3`}>
                        Step-by-step guided process with progress tracking.
                      </p>
                      <ul className={`text-xs ${theme.textSecondary} space-y-1`}>
                        <li>✓ Progress bar</li>
                        <li>✓ One section at a time</li>
                        <li>✓ Next/previous buttons</li>
                        <li>✓ Can't skip required</li>
                      </ul>
                      <div className="mt-3 text-xs font-medium text-purple-600 dark:text-purple-400">
                        → For beginners
                      </div>
                    </div>
                  </div>
                </button>

                {/* Tabbed Mode */}
                <button
                  onClick={() => {
                    setShowModeSelector(false);
                    setShowAddForm(true);
                    setFormMode('tabbed');
                    // Don't reset formData or editingProduct - keep them if editing
                    if (!editingProduct) {
                      setShowCopyFrom(false);
                      setFormData({ name: '', description: '', price: '', category: '', images: [], base_options: {}, custom_options: {}, addons: [], featured: false, file_upload_enabled: true, file_upload_required: false, max_files_allowed: 10, allowed_file_types: 'image/*,.pdf,.stl,.obj,.zip,.rar', max_file_size_mb: 10, cost_breakdown: { materials: [], total_material_cost: 0, labor_cost: 0, overhead_cost: 0, total_cost: 0, profit_margin_percent: 0, suggested_price: 0 }, track_inventory: false, stock_quantity: 0, low_stock_threshold: 5, is_made_to_order: false, weight: 0.0, weight_unit: 'lb', length: 0.0, width: 0.0, height: 0.0, dimension_unit: 'inch', package_type: 'package', declared_value: 0.0, fragile_item: false, signature_required: false, delivery_confirmation: false, prepaid_shipping_enabled: false, selected_flat_rate_id: null });
                    }
                  }}
                  className={`text-left p-6 rounded-xl border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 hover:shadow-lg transition-all`}
                >
                  <div className="flex flex-col gap-3">
                    <div className="text-4xl">📑</div>
                    <div>
                      <h4 className={`text-lg font-bold ${theme.textPrimary} mb-2`}>Tabbed Mode</h4>
                      <p className={`text-sm ${theme.textSecondary} mb-3`}>
                        Organized tabs with quick navigation between sections.
                      </p>
                      <ul className={`text-xs ${theme.textSecondary} space-y-1`}>
                        <li>✓ All tabs visible</li>
                        <li>✓ Jump to any section</li>
                        <li>✓ Full costing model</li>
                        <li>✓ Completion indicators</li>
                      </ul>
                      <div className="mt-3 text-xs font-medium text-blue-600 dark:text-blue-400">
                        → For quick edits
                      </div>
                    </div>
                  </div>
                </button>

                {/* Full Page Mode */}
                <button
                  onClick={() => {
                    setShowModeSelector(false);
                    setShowAddForm(true);
                    setFormMode('fullpage');
                    // Don't reset formData or editingProduct - keep them if editing
                    if (!editingProduct) {
                      setShowCopyFrom(false);
                      setFormData({ name: '', description: '', price: '', category: '', images: [], base_options: {}, custom_options: {}, addons: [], featured: false, file_upload_enabled: true, file_upload_required: false, max_files_allowed: 10, allowed_file_types: 'image/*,.pdf,.stl,.obj,.zip,.rar', max_file_size_mb: 10, cost_breakdown: { materials: [], total_material_cost: 0, labor_cost: 0, overhead_cost: 0, total_cost: 0, profit_margin_percent: 0, suggested_price: 0 }, track_inventory: false, stock_quantity: 0, low_stock_threshold: 5, is_made_to_order: false, weight: 0.0, weight_unit: 'lb', length: 0.0, width: 0.0, height: 0.0, dimension_unit: 'inch', package_type: 'package', declared_value: 0.0, fragile_item: false, signature_required: false, delivery_confirmation: false, prepaid_shipping_enabled: false, selected_flat_rate_id: null });
                    }
                  }}
                  className={`text-left p-6 rounded-xl border-2 border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 hover:shadow-lg transition-all`}
                >
                  <div className="flex flex-col gap-3">
                    <div className="text-4xl">📄</div>
                    <div>
                      <h4 className={`text-lg font-bold ${theme.textPrimary} mb-2`}>Full Page Mode</h4>
                      <p className={`text-sm ${theme.textSecondary} mb-3`}>
                        All sections visible at once on a single scrolling page.
                      </p>
                      <ul className={`text-xs ${theme.textSecondary} space-y-1`}>
                        <li>✓ Everything visible</li>
                        <li>✓ Scroll through all</li>
                        <li>✓ Classic form layout</li>
                        <li>✓ No mode switching</li>
                      </ul>
                      <div className="mt-3 text-xs font-medium text-green-600 dark:text-green-400">
                        → For power users
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Copy Product Option - Only show when adding new product */}
              {!editingProduct && (
              <div className={`p-4 ${theme.bgSecondary} rounded-lg border ${theme.border}`}>
                <h4 className={`text-sm font-semibold ${theme.textPrimary} mb-3 flex items-center gap-2`}>
                  <span>📋</span>
                  <span>Or Copy from Existing Product</span>
                </h4>
                <div className="flex gap-3">
                  <select
                    value={selectedProductToCopy}
                    onChange={(e) => setSelectedProductToCopy(e.target.value)}
                    className={`flex-1 px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                  >
                    <option value="">Select a product to copy...</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - ${product.price} {product.category ? `(${product.category})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      if (selectedProductToCopy) {
                        handleCopyFromProduct(selectedProductToCopy);
                        setShowModeSelector(false);
                        setShowAddForm(true);
                        setFormMode('tabbed');
                      }
                    }}
                    disabled={!selectedProductToCopy}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Copy & Edit
                  </button>
                </div>
                <p className={`text-xs ${theme.textSecondary} mt-2`}>
                  💡 Copies all settings except images. Opens in tabbed mode for editing.
                </p>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddForm && (
        <EnhancedProductForm
          formData={formData}
          setFormData={setFormData}
          categories={categories}
          materials={materials}
          flatRates={flatRates}
          handleSubmit={handleSubmit}
          handleImageUpload={handleImageUpload}
          handleRemoveImage={handleRemoveImage}
          handleAddonImageUpload={handleAddonImageUpload}
          editingProduct={editingProduct}
          uploadingImages={uploadingImages}
          uploadingAddonImages={uploadingAddonImages}
          getImageUrl={getImageUrl}
          formMode={formMode}
          products={products}
        />
      )}

      <div className={`${theme.card} rounded-lg shadow`}>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Products ({products.length})</h3>
          <div className="space-y-4">
            {products.map((product) => (
              <div key={product.id} className={`border ${theme.border} rounded-lg p-4 ${theme.card}`}>
                <div className="flex gap-4">
                  {/* Product Thumbnail */}
                  <div className="flex-shrink-0">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={getImageUrl(product.images[0])}
                        alt={product.name}
                        className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center border-2 border-gray-300 dark:border-gray-600">
                        <span className="text-3xl">📦</span>
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="flex-grow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className={`font-semibold text-lg ${theme.textPrimary}`}>{product.name}</h4>
                        <p className={`${theme.textSecondary} text-sm mt-1 line-clamp-2`}>{product.description}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <p className={`text-sm ${theme.textSecondary}`}>
                            <span className="font-medium">Category:</span> {product.category}
                          </p>
                          {product.images && product.images.length > 1 && (
                            <p className={`text-xs ${theme.textSecondary}`}>
                              📷 {product.images.length} images
                            </p>
                          )}
                        </div>
                        <p className="text-lg font-bold text-green-600 dark:text-green-400 mt-2">
                          ${product.price.toFixed(2)}
                        </p>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(product)}
                          className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-600 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Orders Management Component
export const OrdersManagement = () => {
  const theme = useThemeClasses();
  const [orders, setOrders] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showManualOrderForm, setShowManualOrderForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [editOrderData, setEditOrderData] = useState({});
  const [createOrderData, setCreateOrderData] = useState({
    customerEmail: '',
    productId: '',
    quantity: 1,
    customMessage: ''
  });

  useEffect(() => {
    loadOrders();
    loadProducts();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowViewModal(true);
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setEditOrderData({
      order_status: order.order_status,
      customer_info: { ...order.customer_info },
      shipping_address: order.shipping_address ? { ...order.shipping_address } : {},
      delivery_method: order.delivery_method || 'shipping',
      pickup_location: order.pickup_location || '',
      shipping_service: order.shipping_service || '',
      shipping_cost: order.shipping_cost || 0,
      notes: order.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    try {
      const updatePayload = {
        order_status: editOrderData.order_status,
        customer_info: editOrderData.customer_info,
        shipping_address: editOrderData.shipping_address,
        delivery_method: editOrderData.delivery_method,
        pickup_location: editOrderData.pickup_location,
        shipping_service: editOrderData.shipping_service,
        shipping_cost: parseFloat(editOrderData.shipping_cost) || 0,
        notes: editOrderData.notes
      };

      await axios.put(`${API}/api/admin/orders/${selectedOrder.id}`, updatePayload);
      toast.success('Order updated successfully');
      setShowEditModal(false);
      loadOrders();
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update order');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/api/admin/orders/${orderId}`);
      toast.success('Order deleted successfully');
      loadOrders();
    } catch (error) {
      console.error('Failed to delete order:', error);
      toast.error('Failed to delete order');
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/api/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success('Order status updated successfully');
      loadOrders();
    } catch (error) {
      console.error('Failed to update order status:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        customer_email: createOrderData.customerEmail,
        product_id: createOrderData.productId,
        quantity: createOrderData.quantity.toString(),
        custom_message: createOrderData.customMessage
      });

      const response = await axios.post(`${API}/api/admin/orders/create-for-customer?${params}`);
      
      if (response.data.success) {
        toast.success('Order created and email sent to customer');
        setCreateOrderData({ customerEmail: '', productId: '', quantity: 1, customMessage: '' });
        setShowCreateForm(false);
        loadOrders();
      }
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error('Failed to create order');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'shipped': return 'bg-blue-100 text-blue-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return `${theme.bgSecondary} ${theme.textPrimary}`;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Orders Management</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowManualOrderForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
          >
            📝 Create Manual Order
          </button>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            {showCreateForm ? 'Cancel' : 'Create Order for Customer'}
          </button>
        </div>
      </div>
      
      {showManualOrderForm && (
        <ManualOrderForm
          onClose={() => setShowManualOrderForm(false)}
          onSuccess={(order) => {
            loadOrders();
            setShowManualOrderForm(false);
          }}
        />
      )}

      {showCreateForm && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-4">Create Order for Customer</h3>
          <form onSubmit={handleCreateOrder} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Customer Email</label>
                <input
                  type="email"
                  value={createOrderData.customerEmail}
                  onChange={(e) => setCreateOrderData({...createOrderData, customerEmail: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Product</label>
                <select
                  value={createOrderData.productId}
                  onChange={(e) => setCreateOrderData({...createOrderData, productId: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  required
                >
                  <option value="">Select a product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} - ${product.price.toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Quantity</label>
              <input
                type="number"
                min="1"
                value={createOrderData.quantity}
                onChange={(e) => setCreateOrderData({...createOrderData, quantity: parseInt(e.target.value)})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Custom Message (Optional)</label>
              <textarea
                value={createOrderData.customMessage}
                onChange={(e) => setCreateOrderData({...createOrderData, customMessage: e.target.value})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                rows={3}
              />
            </div>
            <button
              type="submit"
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
            >
              Create Order & Send Email
            </button>
          </form>
        </div>
      )}

      {/* Enhanced Orders Table */}
      <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Orders ({orders.length})</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`${theme.bgSecondary}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Order ID</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Customer</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Total</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Status</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Delivery</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Date</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${theme.card} divide-y divide-gray-200`}>
                {orders.map((order) => (
                  <tr key={order.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700`}>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${theme.textPrimary}`}>
                      #{order.id.slice(-8)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${theme.textPrimary}`}>{order.customer_info.name}</div>
                      <div className={`text-sm ${theme.textSecondary}`}>{order.customer_info.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-bold ${theme.textPrimary}`}>${order.total.toFixed(2)}</div>
                      <div className={`text-sm ${theme.textSecondary}`}>{order.items.length} item(s)</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={order.order_status}
                        onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border-0 ${getStatusColor(order.order_status)}`}
                      >
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                      {order.delivery_method === 'pickup' ? 'Local Pickup' : 'Shipping'}
                      {order.shipping_service && <div className="text-xs">{order.shipping_service}</div>}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleViewOrder(order)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleEditOrder(order)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteOrder(order.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Order View Modal */}
      {showViewModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card} rounded-lg max-w-4xl w-full mx-4 max-h-screen overflow-y-auto`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Order #{selectedOrder.id.slice(-8)}</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className={`${theme.textSecondary} hover:text-blue-600 text-2xl`}
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Information */}
                <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                  <h3 className="font-semibold mb-3">Customer Information</h3>
                  <div className="space-y-2">
                    <p><strong>Name:</strong> {selectedOrder.customer_info.name}</p>
                    <p><strong>Email:</strong> {selectedOrder.customer_info.email}</p>
                    <p><strong>Phone:</strong> {selectedOrder.customer_info.phone || 'Not provided'}</p>
                  </div>
                </div>

                {/* Order Details */}
                <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                  <h3 className="font-semibold mb-3">Order Details</h3>
                  <div className="space-y-2">
                    <p><strong>Status:</strong> 
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${getStatusColor(selectedOrder.order_status)}`}>
                        {selectedOrder.order_status}
                      </span>
                    </p>
                    <p><strong>Order Date:</strong> {new Date(selectedOrder.created_at).toLocaleString()}</p>
                    <p><strong>Order Number:</strong> {selectedOrder.order_number || selectedOrder.id.slice(-8)}</p>
                    <p><strong>Subtotal:</strong> ${(selectedOrder.subtotal || selectedOrder.total).toFixed(2)}</p>
                    {selectedOrder.tax > 0 && (
                      <p><strong>Tax:</strong> ${selectedOrder.tax.toFixed(2)}</p>
                    )}
                    {selectedOrder.shipping_cost > 0 && (
                      <p><strong>Shipping:</strong> ${selectedOrder.shipping_cost.toFixed(2)}</p>
                    )}
                    <p className="text-lg"><strong>Total:</strong> <span className="text-blue-600 font-bold">${selectedOrder.total.toFixed(2)}</span></p>
                    <p><strong>Payment Method:</strong> {selectedOrder.payment_method === 'etransfer' ? 'E-Transfer' : selectedOrder.payment_method === 'paypal' ? 'PayPal' : selectedOrder.payment_method || 'Not specified'}</p>
                    <p><strong>Payment Status:</strong> 
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ml-2 ${
                        selectedOrder.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                        selectedOrder.payment_status === 'partially_paid' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedOrder.payment_status || 'pending'}
                      </span>
                    </p>
                    {selectedOrder.delivery_method && (
                      <p><strong>Delivery Method:</strong> {selectedOrder.delivery_method === 'shipping' ? 'Shipping' : 'Local Pickup'}</p>
                    )}
                  </div>
                </div>

                {/* Shipping Information */}
                {selectedOrder.delivery_method === 'shipping' && selectedOrder.shipping_address && (
                  <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                    <h3 className="font-semibold mb-3">Shipping Information</h3>
                    <div className="space-y-2">
                      <p><strong>Service:</strong> {selectedOrder.shipping_service || 'Standard'}</p>
                      <p><strong>Cost:</strong> ${selectedOrder.shipping_cost?.toFixed(2) || '0.00'}</p>
                      <p><strong>Address:</strong></p>
                      <div className="ml-4 text-sm">
                        <p>{selectedOrder.shipping_address.street}</p>
                        <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.province} {selectedOrder.shipping_address.postal_code}</p>
                        <p>{selectedOrder.shipping_address.country}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pickup Information */}
                {selectedOrder.delivery_method === 'pickup' && (
                  <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                    <h3 className="font-semibold mb-3">Pickup Information</h3>
                    <div className="space-y-2">
                      <p><strong>Location:</strong> {selectedOrder.pickup_location || 'Cambridge, Ontario'}</p>
                      <p><strong>Cost:</strong> Free</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Items */}
              <div className="mt-6">
                <h3 className="font-semibold mb-3">Order Items</h3>
                <div className="space-y-4">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium">{item.product_name || 'Product'}</h4>
                          <p className={`text-sm ${theme.textSecondary}`}>Quantity: {item.quantity}</p>
                          <p className={`text-sm ${theme.textSecondary}`}>Price: ${item.price.toFixed(2)}</p>
                          
                          {/* Selected Options */}
                          {item.selected_options && Object.keys(item.selected_options).length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm font-medium">Options:</p>
                              <div className={`text-sm ${theme.textSecondary}`}>
                                {Object.entries(item.selected_options).map(([key, value]) => (
                                  <span key={key} className={`inline-block ${theme.bgSecondary} px-2 py-1 rounded mr-2 mb-1`}>
                                    {key}: {value}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Custom Text Inputs */}
                          {item.custom_text_inputs && Object.keys(item.custom_text_inputs).length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm font-medium">Custom Text:</p>
                              <div className={`text-sm ${theme.textSecondary}`}>
                                {Object.entries(item.custom_text_inputs).map(([key, value]) => (
                                  <p key={key}><strong>{key}:</strong> {value}</p>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Selected Addons */}
                          {item.selected_addons && item.selected_addons.length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm font-medium">Addons:</p>
                              <div className={`text-sm ${theme.textSecondary}`}>
                                {item.selected_addons.map((addon, addonIndex) => (
                                  <span key={addonIndex} className="inline-block bg-blue-100 px-2 py-1 rounded mr-2 mb-1">
                                    {addon}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Customization Notes */}
                          {item.customization_notes && (
                            <div className="mt-2">
                              <p className="text-sm font-medium">Customization Notes:</p>
                              <div className={`text-sm ${theme.textSecondary} bg-yellow-50 p-2 rounded mt-1`}>
                                <p className="whitespace-pre-wrap">{item.customization_notes}</p>
                              </div>
                            </div>
                          )}

                          {/* Uploaded Files */}
                          {item.uploaded_files && item.uploaded_files.length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm font-medium mb-2">Customer Uploaded Files:</p>
                              <div className="grid grid-cols-1 gap-2">
                                {item.uploaded_files.map((file, fileIndex) => {
                                  const isImage = file.content_type?.startsWith('image/');
                                  const fileUrl = file.file_url || `/api/files/${file.filename}`;
                                  const displayName = file.original_filename || file.filename || `File ${fileIndex + 1}`;
                                  
                                  return (
                                    <div key={fileIndex} className={`flex items-center justify-between p-2 rounded ${theme.bgSecondary} border ${theme.border}`}>
                                      <div className="flex items-center gap-3 flex-1">
                                        {/* Thumbnail or icon */}
                                        {isImage && file.thumbnail_url ? (
                                          <img 
                                            src={`${process.env.REACT_APP_BACKEND_URL}${file.thumbnail_url}`}
                                            alt={displayName}
                                            className="w-12 h-12 object-cover rounded border"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'flex';
                                            }}
                                          />
                                        ) : null}
                                        <div className={`w-12 h-12 flex items-center justify-center rounded ${isImage ? 'hidden' : 'flex'} ${theme.bgPrimary}`}>
                                          <span className="text-2xl">{isImage ? '🖼️' : '📄'}</span>
                                        </div>
                                        
                                        {/* File info */}
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm font-medium truncate ${theme.textPrimary}`} title={displayName}>
                                            {displayName}
                                          </p>
                                          {file.file_size && (
                                            <p className={`text-xs ${theme.textSecondary}`}>
                                              {(file.file_size / 1024).toFixed(1)} KB
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Action buttons */}
                                      <div className="flex gap-2">
                                        {isImage && (
                                          <a
                                            href={`${process.env.REACT_APP_BACKEND_URL}${fileUrl}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`px-3 py-1 text-xs rounded ${theme.button} hover:opacity-80 transition`}
                                            title="View image"
                                          >
                                            👁️ View
                                          </a>
                                        )}
                                        <a
                                          href={`${process.env.REACT_APP_BACKEND_URL}${fileUrl}`}
                                          download={displayName}
                                          className={`px-3 py-1 text-xs rounded bg-green-500 text-white hover:bg-green-600 transition`}
                                          title="Download file"
                                        >
                                          ⬇️ Download
                                        </a>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold">${(item.price * item.quantity).toFixed(2)}</p>
                          {item.option_total > 0 && (
                            <p className={`text-sm ${theme.textSecondary}`}>Options: +${item.option_total.toFixed(2)}</p>
                          )}
                          {item.addon_total > 0 && (
                            <p className={`text-sm ${theme.textSecondary}`}>Addons: +${item.addon_total.toFixed(2)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Notes */}
              {selectedOrder.notes && (
                <div className="mt-6 bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Order Notes</h3>
                  <p className={`text-sm ${theme.textPrimary}`}>{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Edit Modal */}
      {showEditModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card} rounded-lg max-w-4xl w-full mx-4 max-h-screen overflow-y-auto`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Edit Order #{selectedOrder.id.slice(-8)}</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className={`${theme.textSecondary} hover:text-blue-600 text-2xl`}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleUpdateOrder} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Order Status */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Order Status</label>
                    <select
                      value={editOrderData.order_status}
                      onChange={(e) => setEditOrderData({...editOrderData, order_status: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Delivery Method */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Delivery Method</label>
                    <select
                      value={editOrderData.delivery_method}
                      onChange={(e) => setEditOrderData({...editOrderData, delivery_method: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="shipping">Shipping</option>
                      <option value="pickup">Local Pickup</option>
                    </select>
                  </div>
                </div>

                {/* Customer Information */}
                <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                  <h3 className="font-semibold mb-3">Customer Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Name</label>
                      <input
                        type="text"
                        value={editOrderData.customer_info?.name || ''}
                        onChange={(e) => setEditOrderData({
                          ...editOrderData,
                          customer_info: {...editOrderData.customer_info, name: e.target.value}
                        })}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email</label>
                      <input
                        type="email"
                        value={editOrderData.customer_info?.email || ''}
                        onChange={(e) => setEditOrderData({
                          ...editOrderData,
                          customer_info: {...editOrderData.customer_info, email: e.target.value}
                        })}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Phone</label>
                      <input
                        type="text"
                        value={editOrderData.customer_info?.phone || ''}
                        onChange={(e) => setEditOrderData({
                          ...editOrderData,
                          customer_info: {...editOrderData.customer_info, phone: e.target.value}
                        })}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Information (only show if delivery method is shipping) */}
                {editOrderData.delivery_method === `shipping` && (
                  <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                    <h3 className="font-semibold mb-3">Shipping Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Shipping Service</label>
                        <input
                          type="text"
                          value={editOrderData.shipping_service || ''}
                          onChange={(e) => setEditOrderData({...editOrderData, shipping_service: e.target.value})}
                          className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          placeholder="e.g., Regular Parcel, Expedited"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Shipping Cost ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editOrderData.shipping_cost || ''}
                          onChange={(e) => setEditOrderData({...editOrderData, shipping_cost: e.target.value})}
                          className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div className="mt-4">
                      <h4 className="font-medium mb-2">Shipping Address</h4>
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editOrderData.shipping_address?.street || ''}
                          onChange={(e) => setEditOrderData({
                            ...editOrderData,
                            shipping_address: {...editOrderData.shipping_address, street: e.target.value}
                          })}
                          placeholder="Street Address"
                          className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={editOrderData.shipping_address?.city || ''}
                            onChange={(e) => setEditOrderData({
                              ...editOrderData,
                              shipping_address: {...editOrderData.shipping_address, city: e.target.value}
                            })}
                            placeholder="City"
                            className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                          <input
                            type="text"
                            value={editOrderData.shipping_address?.province || ''}
                            onChange={(e) => setEditOrderData({
                              ...editOrderData,
                              shipping_address: {...editOrderData.shipping_address, province: e.target.value}
                            })}
                            placeholder="Province/State"
                            className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={editOrderData.shipping_address?.postal_code || ''}
                            onChange={(e) => setEditOrderData({
                              ...editOrderData,
                              shipping_address: {...editOrderData.shipping_address, postal_code: e.target.value}
                            })}
                            placeholder="Postal Code"
                            className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                          <input
                            type="text"
                            value={editOrderData.shipping_address?.country || ''}
                            onChange={(e) => setEditOrderData({
                              ...editOrderData,
                              shipping_address: {...editOrderData.shipping_address, country: e.target.value}
                            })}
                            placeholder="Country"
                            className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pickup Information (only show if delivery method is pickup) */}
                {editOrderData.delivery_method === `pickup` && (
                  <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                    <h3 className="font-semibold mb-3">Pickup Information</h3>
                    <div>
                      <label className="block text-sm font-medium mb-2">Pickup Location</label>
                      <input
                        type="text"
                        value={editOrderData.pickup_location || ''}
                        onChange={(e) => setEditOrderData({...editOrderData, pickup_location: e.target.value})}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="e.g., Cambridge, Ontario"
                      />
                    </div>
                  </div>
                )}

                {/* Order Notes */}
                <div>
                  <label className="block text-sm font-medium mb-2">Order Notes</label>
                  <textarea
                    value={editOrderData.notes || ''}
                    onChange={(e) => setEditOrderData({...editOrderData, notes: e.target.value})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    rows={4}
                    placeholder="Internal notes about this order..."
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className={`px-6 py-2 border ${theme.border} rounded ${theme.textPrimary} hover:bg-gray-100 dark:hover:bg-gray-700`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Update Order
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Customers Management Component  
export const CustomersManagement = () => {
  const theme = useThemeClasses();
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'settings'
  const [customers, setCustomers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [editCustomerData, setEditCustomerData] = useState({});
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkRole, setBulkRole] = useState('customer');
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // Confirmation dialog states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  
  // New Customer Settings State
  const [newCustomerSettings, setNewCustomerSettings] = useState({
    new_customer_promotion_enabled: false,
    new_customer_promotion_code: '',
    send_welcome_email: true
  });
  const [promotions, setPromotions] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  const userRoles = [
    { value: 'customer', label: 'Customer', color: 'bg-blue-100 text-blue-800', description: 'Regular shopping privileges' },
    { value: 'staff', label: 'Staff', color: 'bg-green-100 text-green-800', description: 'Limited admin access' },
    { value: 'admin', label: 'Administrator', color: 'bg-purple-100 text-purple-800', description: 'Full admin access' }
  ];

  useEffect(() => {
    loadCustomers();
    loadNewCustomerSettings();
    loadPromotions();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`);
      // Show all customers including those with admin roles
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
      toast.error('Failed to load customers');
    }
  };
  
  const loadNewCustomerSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/new-customer-settings`);
      setNewCustomerSettings(response.data || {
        new_customer_promotion_enabled: false,
        new_customer_promotion_code: '',
        send_welcome_email: true
      });
    } catch (error) {
      console.error('Failed to load new customer settings:', error);
    }
  };
  
  const loadPromotions = async () => {
    try {
      const response = await axios.get(`${API}/admin/promotions`);
      setPromotions(response.data || []);
    } catch (error) {
      console.error('Failed to load promotions:', error);
    }
  };
  
  const handleSaveNewCustomerSettings = async () => {
    try {
      setSavingSettings(true);
      await axios.put(`${API}/admin/new-customer-settings`, newCustomerSettings);
      toast.success('New customer settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const loadCustomerOrders = async (customerId) => {
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}/orders`);
      setCustomerOrders(response.data);
    } catch (error) {
      console.error('Failed to load customer orders:', error);
      setCustomerOrders([]);
    }
  };

  const handleViewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setShowViewModal(true);
    await loadCustomerOrders(customer.id);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setEditCustomerData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      postal: customer.postal || '',
      country: customer.country || '',
      notes: customer.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/customers/${selectedCustomer.id}`, editCustomerData);
      toast.success('Customer updated successfully');
      setShowEditModal(false);
      loadCustomers();
    } catch (error) {
      console.error('Failed to update customer:', error);
      toast.error('Failed to update customer');
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        name: newCustomer.name,
        email: newCustomer.email,
        phone: newCustomer.phone
      });

      await axios.post(`${API}/admin/customers?${params}`);
      toast.success('Customer added successfully');
      setNewCustomer({ name: '', email: '', phone: '' });
      setShowAddForm(false);
      loadCustomers();
    } catch (error) {
      console.error('Failed to add customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to add customer');
    }
  };

  const handleDeleteCustomer = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/admin/customers/${customerToDelete.id}?delete_orders=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer and associated orders deleted successfully');
      setShowDeleteConfirm(false);
      setCustomerToDelete(null);
      loadCustomers();
    } catch (error) {
      console.error('Failed to delete customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const handleRoleChange = async (customerId, newRole) => {
    try {
      await axios.put(`${API}/admin/customers/${customerId}/role`, { role: newRole });
      toast.success('Customer role updated successfully');
      loadCustomers();
    } catch (error) {
      console.error('Failed to update customer role:', error);
      toast.error('Failed to update customer role');
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(customer => customer.id));
    }
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/admin/customers/bulk-delete`, {
        customer_ids: selectedCustomers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Successfully deleted ${response.data.deleted_count} customers`);
      setSelectedCustomers([]);
      setShowBulkActions(false);
      setShowBulkDeleteConfirm(false);
      loadCustomers();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const handleBulkRoleUpdate = async () => {
    if (!window.confirm(`Are you sure you want to update ${selectedCustomers.length} customers to ${bulkRole} role?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/customers/bulk-update-role`, {
        customer_ids: selectedCustomers,
        role: bulkRole
      });

      toast.success(`Successfully updated ${response.data.updated_count} customers to ${bulkRole} role`);
      setSelectedCustomers([]);
      setShowBulkActions(false);
      loadCustomers();
    } catch (error) {
      console.error('Bulk role update failed:', error);
      toast.error(error.response?.data?.detail || 'Bulk role update failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Customers Management</h2>
      </div>

      {/* Tab Navigation */}
      <div className={`border-b ${theme.border}`}>
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('list')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'list'
                ? 'border-blue-500 text-blue-600'
                : `border-transparent ${theme.textSecondary} hover:text-blue-600`
            }`}
          >
            👥 Customer List
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : `border-transparent ${theme.textSecondary} hover:text-blue-600`
            }`}
          >
            🎁 New Customer Settings
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'list' ? (
        <>
          <div className="flex justify-between items-center">
            <div className={`${theme.textSecondary}`}>Manage your customers and their roles</div>
            <div className="flex space-x-3">
              {selectedCustomers.length > 0 && (
                <button
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700"
                >
                  Bulk Actions ({selectedCustomers.length})
                </button>
              )}
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {showAddForm ? 'Cancel' : 'Add Customer'}
              </button>
            </div>
          </div>

      {/* Bulk Actions Panel */}
      {showBulkActions && selectedCustomers.length > 0 && (
        <div className={`${theme.card} p-6 rounded-lg shadow border-l-4 border-orange-500`}>
          <h3 className="text-lg font-semibold mb-4 text-orange-800">Bulk Actions ({selectedCustomers.length} customers selected)</h3>
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium">Change Role to:</label>
              <select
                value={bulkRole}
                onChange={(e) => setBulkRole(e.target.value)}
                className={`px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                {userRoles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
              <button
                onClick={handleBulkRoleUpdate}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Update Roles
              </button>
            </div>
            
            <button
              onClick={handleBulkDelete}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Delete Selected
            </button>
            
            <button
              onClick={() => {
                setSelectedCustomers([]);
                setShowBulkActions(false);
              }}
              className={`${theme.bgSecondary}0 text-white px-4 py-2 rounded hover:bg-gray-600`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-4">Add New Customer</h3>
          <form onSubmit={handleAddCustomer} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Phone (Optional)</label>
              <input
                type="tel"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
            <button
              type="submit"
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
            >
              Add Customer
            </button>
          </form>
        </div>
      )}

      {/* Enhanced Customers Table */}
      <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Customers ({customers.length})</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`${theme.bgSecondary}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>
                    <input
                      type="checkbox"
                      checked={selectedCustomers.length === customers.length && customers.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Customer</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Contact</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Role</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Location</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Orders</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Joined</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${theme.card} divide-y divide-gray-200`}>
                {customers.map((customer) => {
                  const customerRole = userRoles.find(role => role.value === (customer.role || `customer`));
                  return (
                    <tr key={customer.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700 ${selectedCustomers.includes(customer.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedCustomers.includes(customer.id)}
                          onChange={() => handleSelectCustomer(customer.id)}
                          className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-medium">{customer.name?.charAt(0) || 'U'}</span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className={`text-sm font-medium ${theme.textPrimary}`}>{customer.name}</div>
                            <div className={`text-sm ${theme.textSecondary}`}>ID: {customer.id?.slice(-8)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${theme.textPrimary}`}>{customer.email}</div>
                        <div className={`text-sm ${theme.textSecondary}`}>{customer.phone || 'No phone'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={customer.role || 'customer'}
                          onChange={(e) => handleRoleChange(customer.id, e.target.value)}
                          className={`px-3 py-1 text-sm font-medium rounded-full border-0 cursor-pointer ${customerRole?.color || `${theme.bgSecondary} ${theme.textPrimary}`}`}
                        >
                          {userRoles.map(role => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                          ))}
                        </select>
                        <div className={`text-xs ${theme.textSecondary} mt-1`}>{customerRole?.description || 'Regular customer'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${theme.textPrimary}`}>{customer.city || 'Unknown'}</div>
                        <div className={`text-sm ${theme.textSecondary}`}>{customer.country || 'Unknown'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium ${theme.textPrimary}`}>{customer.total_orders || 0}</div>
                        <div className={`text-sm ${theme.textSecondary}`}>${(customer.total_spent || 0).toFixed(2)} spent</div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                        {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : `Unknown`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleViewCustomer(customer)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleEditCustomer(customer)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCustomer(customer)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Customer View Modal */}
      {showViewModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card} rounded-lg max-w-4xl w-full mx-4 max-h-screen overflow-y-auto`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Customer Details</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className={`${theme.textSecondary} hover:text-blue-600 text-2xl`}
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Information */}
                <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                  <h3 className="font-semibold mb-3">Customer Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                        <span className="text-xl font-medium text-blue-600">
                          {selectedCustomer.name ? selectedCustomer.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold">{selectedCustomer.name}</h4>
                        <p className={`text-sm ${theme.textSecondary}`}>Customer ID: {selectedCustomer.id.slice(-8)}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p><strong>Email:</strong> {selectedCustomer.email}</p>
                      <p><strong>Phone:</strong> {selectedCustomer.phone || 'Not provided'}</p>
                      <p><strong>Joined:</strong> {new Date(selectedCustomer.created_at).toLocaleDateString()}</p>
                      {selectedCustomer.last_login && (
                        <p><strong>Last Login:</strong> {new Date(selectedCustomer.last_login).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                  <h3 className="font-semibold mb-3">Address Information</h3>
                  <div className="space-y-2">
                    {selectedCustomer.address ? (
                      <div>
                        <p>{selectedCustomer.address}</p>
                        <p>{selectedCustomer.city}, {selectedCustomer.postal}</p>
                        <p>{selectedCustomer.country}</p>
                      </div>
                    ) : (
                      <p className={`${theme.textSecondary} italic`}>No address information available</p>
                    )}
                  </div>
                </div>

                {/* Account Statistics */}
                <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                  <h3 className="font-semibold mb-3">Account Statistics</h3>
                  <div className="space-y-2">
                    <p><strong>Total Orders:</strong> {selectedCustomer.total_orders || 0}</p>
                    <p><strong>Total Spent:</strong> ${(selectedCustomer.total_spent || 0).toFixed(2)}</p>
                    <p><strong>Average Order:</strong> ${selectedCustomer.total_orders > 0 ? (selectedCustomer.total_spent / selectedCustomer.total_orders).toFixed(2) : `0.00`}</p>
                    <p><strong>Account Status:</strong> 
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 ml-2">
                        Active
                      </span>
                    </p>
                  </div>
                </div>

                {/* Account Settings */}
                <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                  <h3 className="font-semibold mb-3">Account Settings</h3>
                  <div className="space-y-2">
                    <p><strong>Email Notifications:</strong> {selectedCustomer.email_notifications !== false ? 'Enabled' : 'Disabled'}</p>
                    <p><strong>Marketing Emails:</strong> {selectedCustomer.marketing_emails !== false ? 'Subscribed' : 'Unsubscribed'}</p>
                    <p><strong>Preferred Language:</strong> {selectedCustomer.language || 'English'}</p>
                  </div>
                </div>
              </div>

              {/* Customer Orders */}
              <div className="mt-6">
                <h3 className="font-semibold mb-3">Recent Orders</h3>
                <div className={`${theme.bgSecondary} rounded-lg p-4`}>
                  {customerOrders.length > 0 ? (
                    <div className="space-y-3">
                      {customerOrders.slice(0, 5).map((order) => (
                        <div key={order.id} className={`${theme.card} p-3 rounded border`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">Order #{order.id.slice(-8)}</p>
                              <p className={`text-sm ${theme.textSecondary}`}>{new Date(order.created_at).toLocaleDateString()}</p>
                              <p className={`text-sm ${theme.textSecondary}`}>{order.items.length} item(s)</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">${order.total.toFixed(2)}</p>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                order.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                                order.order_status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                                order.order_status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {order.order_status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {customerOrders.length > 5 && (
                        <p className={`text-sm ${theme.textSecondary} text-center`}>
                          ... and {customerOrders.length - 5} more orders
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className={`${theme.textSecondary} italic`}>No orders found for this customer</p>
                  )}
                </div>
              </div>

              {/* Customer Notes */}
              {selectedCustomer.notes && (
                <div className="mt-6 bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Admin Notes</h3>
                  <p className={`text-sm ${theme.textPrimary}`}>{selectedCustomer.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Customer Edit Modal */}
      {showEditModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card} rounded-lg max-w-4xl w-full mx-4 max-h-screen overflow-y-auto`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Edit Customer</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className={`${theme.textSecondary} hover:text-blue-600 text-2xl`}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleUpdateCustomer} className="space-y-6">
                {/* Basic Information */}
                <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                  <h3 className="font-semibold mb-3">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Full Name</label>
                      <input
                        type="text"
                        value={editCustomerData.name || ''}
                        onChange={(e) => setEditCustomerData({...editCustomerData, name: e.target.value})}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email Address</label>
                      <input
                        type="email"
                        value={editCustomerData.email || ''}
                        onChange={(e) => setEditCustomerData({...editCustomerData, email: e.target.value})}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Phone Number</label>
                      <input
                        type="tel"
                        value={editCustomerData.phone || ''}
                        onChange={(e) => setEditCustomerData({...editCustomerData, phone: e.target.value})}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="e.g., +1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                  <h3 className="font-semibold mb-3">Address Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Street Address</label>
                      <input
                        type="text"
                        value={editCustomerData.address || ''}
                        onChange={(e) => setEditCustomerData({...editCustomerData, address: e.target.value})}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="123 Main Street, Apt 4B"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">City</label>
                        <input
                          type="text"
                          value={editCustomerData.city || ''}
                          onChange={(e) => setEditCustomerData({...editCustomerData, city: e.target.value})}
                          className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          placeholder="Toronto"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Postal Code</label>
                        <input
                          type="text"
                          value={editCustomerData.postal || ''}
                          onChange={(e) => setEditCustomerData({...editCustomerData, postal: e.target.value})}
                          className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          placeholder="M5V 3A8"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Country</label>
                        <input
                          type="text"
                          value={editCustomerData.country || ''}
                          onChange={(e) => setEditCustomerData({...editCustomerData, country: e.target.value})}
                          className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          placeholder="Canada"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Admin Notes */}
                <div className={`${theme.bgSecondary} p-4 rounded-lg`}>
                  <h3 className="font-semibold mb-3">Admin Notes</h3>
                  <div>
                    <label className="block text-sm font-medium mb-2">Internal Notes</label>
                    <textarea
                      value={editCustomerData.notes || ''}
                      onChange={(e) => setEditCustomerData({...editCustomerData, notes: e.target.value})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      rows={4}
                      placeholder="Internal notes about this customer (not visible to customer)..."
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className={`px-6 py-2 border ${theme.border} rounded ${theme.textPrimary} hover:bg-gray-100 dark:hover:bg-gray-700`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Update Customer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
        </>
      ) : (
        /* New Customer Settings Tab */
        <div className="space-y-6">
          <div className={`${theme.card} p-6 rounded-lg shadow`}>
            <h3 className="text-lg font-semibold mb-4">Welcome Email & Promotions for New Customers</h3>
            
            <div className="space-y-6">
              {/* Welcome Email Toggle */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Send Welcome Email</label>
                  <p className={`text-sm ${theme.textSecondary}`}>
                    Automatically send a welcome email when new customers complete their account setup (billing/shipping addresses)
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={newCustomerSettings.send_welcome_email}
                    onChange={(e) => setNewCustomerSettings(prev => ({ ...prev, send_welcome_email: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600`}></div>
                </label>
              </div>

              {/* New Customer Promotion */}
              <div className="border-t pt-6">
                <h4 className="text-md font-semibold mb-4">New Customer Promotion</h4>
                
                <div className="space-y-4">
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">Enable New Customer Promotion</label>
                      <p className={`text-sm ${theme.textSecondary}`}>
                        Automatically apply a promotion code for new customers
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={newCustomerSettings.new_customer_promotion_enabled}
                        onChange={(e) => setNewCustomerSettings(prev => ({ ...prev, new_customer_promotion_enabled: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600`}></div>
                    </label>
                  </div>

                  {/* Promotion Code Selection */}
                  {newCustomerSettings.new_customer_promotion_enabled && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Select Promotion Code</label>
                      <select
                        value={newCustomerSettings.new_customer_promotion_code || ''}
                        onChange={(e) => setNewCustomerSettings(prev => ({ ...prev, new_customer_promotion_code: e.target.value }))}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-purple-500`}
                      >
                        <option value="">Select a promotion...</option>
                        {promotions.filter(p => p.is_active).map((promo, index) => (
                          <option key={index} value={promo.code}>
                            {promo.code} - {promo.description} 
                            ({promo.discount_type === `percentage` ? `${promo.discount_value}%` : `$${promo.discount_value}`} off)
                          </option>
                        ))}
                      </select>
                      
                      {newCustomerSettings.new_customer_promotion_code && (
                        <div className="mt-3 p-3 bg-purple-100 rounded-lg">
                          <p className="text-sm text-purple-800">
                            ✅ Selected promotion will be automatically applied to new customers
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Info Box */}
              <div className={`${theme.bgSecondary} p-4 rounded-lg border-l-4 border-blue-500`}>
                <h4 className={`font-semibold ${theme.textPrimary} mb-2`}>ℹ️ How it works</h4>
                <ul className={`text-sm ${theme.textSecondary} space-y-1 list-disc list-inside`}>
                  <li>Welcome emails are sent when customers add their billing/shipping addresses</li>
                  <li>Emails include your logo, featured products, and company information</li>
                  <li>New customer promotions are automatically applied at checkout</li>
                  <li>Make sure to create promotions in the Marketing section first</li>
                </ul>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveNewCustomerSettings}
                  disabled={savingSettings}
                  className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Customer Confirmation Dialog */}
      {showDeleteConfirm && customerToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card} rounded-lg shadow-xl max-w-md w-full mx-4 p-6`}>
            <h3 className={`text-xl font-bold mb-4 ${theme.textPrimary}`}>
              Confirm Delete Customer
            </h3>
            <p className={`mb-6 ${theme.textSecondary}`}>
              Are you sure you want to delete <strong>{customerToDelete.name}</strong>? 
              This action cannot be undone and will remove all customer data.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setCustomerToDelete(null);
                }}
                className={`px-6 py-2 border ${theme.border} rounded-lg ${theme.textPrimary} hover:${theme.bgSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteCustomer}
                className="px-6 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700"
              >
                Delete Customer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card} rounded-lg shadow-xl max-w-md w-full mx-4 p-6`}>
            <h3 className={`text-xl font-bold mb-4 ${theme.textPrimary}`}>
              Confirm Bulk Delete
            </h3>
            <p className={`mb-6 ${theme.textSecondary}`}>
              Are you sure you want to delete <strong>{selectedCustomers.length} customer{selectedCustomers.length > 1 ? 's' : ''}</strong>? 
              This action cannot be undone and will remove all customer data.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className={`px-6 py-2 border ${theme.border} rounded-lg ${theme.textPrimary} hover:${theme.bgSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                className="px-6 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700"
              >
                Delete {selectedCustomers.length} Customer{selectedCustomers.length > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Shipping Settings Component
export const ShippingSettings = () => {
  const theme = useThemeClasses();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingPayPal, setTestingPayPal] = useState(false);
  // Flat Rate Shipping State
  const [flatRates, setFlatRates] = useState([]);
  const [showAddFlatRate, setShowAddFlatRate] = useState(false);
  const [editingFlatRate, setEditingFlatRate] = useState(null);
  const [flatRateData, setFlatRateData] = useState({
    name: '',
    description: '',
    price: 0.0,
    min_weight: 0.0,
    max_weight: 999.0,
    weight_unit: 'lb',
    max_length: 999.0,
    max_width: 999.0,
    max_height: 999.0,
    dimension_unit: 'inch',
    package_types: ['package'],
    delivery_time: '3-7 business days',
    is_active: true,
    order: 0
  });

  useEffect(() => {
    loadSettings();
    loadFlatRates();
  }, []);

  const loadFlatRates = async () => {
    try {
      const response = await axios.get(`${API}/admin/flat-rates`);
      setFlatRates(response.data);
    } catch (error) {
      console.error('Failed to load flat rates:', error);
      toast.error('Failed to load flat rate shipping options');
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formData = new FormData(e.target);
      const shippingSettings = {
        shipping_method: formData.get('shipping_method') || 'canada_post',
        canada_post_enabled: formData.get('canada_post_enabled') === 'on',
        canada_post_customer_number: formData.get('canada_post_customer_number'),
        canada_post_contract_id: formData.get('canada_post_contract_id'),
        canada_post_api_key: formData.get('canada_post_api_key'),
        canada_post_environment: formData.get('canada_post_environment'),
        canada_post_fuel_surcharge: parseFloat(formData.get('canada_post_fuel_surcharge')) || 0.0,
        free_shipping_threshold: formData.get('free_shipping_threshold') ? parseFloat(formData.get('free_shipping_threshold')) : null,
        handling_fee: parseFloat(formData.get('handling_fee')) || 0,
        origin_postal_code: formData.get('origin_postal_code'),
        // Fallback Rate Settings
        fallback_base_rate: parseFloat(formData.get('fallback_base_rate')) || 12.00,
        fallback_weight_multiplier: parseFloat(formData.get('fallback_weight_multiplier')) || 3.50,
        fallback_size_multiplier: parseFloat(formData.get('fallback_size_multiplier')) || 0.50,
        fallback_regular_multiplier: parseFloat(formData.get('fallback_regular_multiplier')) || 1.0,
        fallback_expedited_multiplier: parseFloat(formData.get('fallback_expedited_multiplier')) || 1.6,
        fallback_xpress_multiplier: parseFloat(formData.get('fallback_xpress_multiplier')) || 2.1,
        fallback_priority_multiplier: parseFloat(formData.get('fallback_priority_multiplier')) || 3.0
      };

      const paymentSettings = {
        paypal_enabled: formData.get('paypal_enabled') === 'on',
        paypal_client_id: formData.get('paypal_client_id'),
        paypal_client_secret: formData.get('paypal_client_secret'),
        paypal_environment: formData.get('paypal_environment'),
        paypal_business_email: formData.get('paypal_business_email'),
        etransfer_enabled: formData.get('etransfer_enabled') === 'on',
        etransfer_email: formData.get('etransfer_email'),
        currency: formData.get('currency')
      };

      const response = await axios.put(`${API}/settings`, {
        shipping_settings: shippingSettings,
        payment_settings: paymentSettings
      });

      if (response.data.success) {
        toast.success('Settings updated successfully');
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const testCanadaPostConnection = async () => {
    setTestingConnection(true);
    
    try {
      const formData = new FormData(document.querySelector('form'));
      const testData = {
        customer_number: formData.get('canada_post_customer_number'),
        contract_id: formData.get('canada_post_contract_id'),
        api_key: formData.get('canada_post_api_key'),
        environment: formData.get('canada_post_environment')
      };
      
      if (!testData.customer_number || !testData.contract_id || !testData.api_key) {
        toast.error('Please fill in Customer Number, Contract ID, and API Key before testing');
        return;
      }
      
      const response = await axios.post(`${API}/shipping/test-connection`, testData);
      
      if (response.data.success) {
        toast.success(`✅ Canada Post SOAP connection successful! Environment: ${response.data.environment}`);
      } else {
        toast.error('Canada Post SOAP connection failed');
      }
    } catch (error) {
      console.error('Canada Post SOAP test failed:', error);
      toast.error(error.response?.data?.detail || 'Canada Post SOAP connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const testPayPalConnection = async () => {
    setTestingPayPal(true);
    
    try {
      const formData = new FormData(document.querySelector('form'));
      const testData = {
        client_id: formData.get('paypal_client_id'),
        client_secret: formData.get('paypal_client_secret'),
        environment: formData.get('paypal_environment'),
        business_email: formData.get('paypal_business_email')
      };
      
      if (!testData.client_id || !testData.client_secret) {
        toast.error('Please fill in PayPal Client ID and Client Secret before testing');
        return;
      }
      
      const response = await axios.post(`${API}/payment/paypal/test-connection`, testData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ PayPal connection successful! Environment: ${response.data.environment}`);
      } else {
        toast.error('PayPal connection failed');
      }
    } catch (error) {
      console.error('PayPal test failed:', error);
      toast.error(error.response?.data?.detail || 'PayPal connection test failed');
    } finally {
      setTestingPayPal(false);
    }
  };

  // Flat Rate Management Functions
  const handleSaveFlatRate = async () => {
    try {
      if (editingFlatRate) {
        await axios.put(`${API}/admin/flat-rates/${editingFlatRate.id}`, flatRateData);
        toast.success('Flat rate updated successfully');
      } else {
        await axios.post(`${API}/admin/flat-rates`, flatRateData);
        toast.success('Flat rate added successfully');
      }

      setShowAddFlatRate(false);
      setEditingFlatRate(null);
      setFlatRateData({
        name: '', description: '', price: 0.0, min_weight: 0.0, max_weight: 999.0, weight_unit: 'lb',
        max_length: 999.0, max_width: 999.0, max_height: 999.0, dimension_unit: 'inch',
        package_types: ['package'], delivery_time: '3-7 business days', is_active: true, order: 0
      });
      loadFlatRates();
    } catch (error) {
      console.error('Failed to save flat rate:', error);
      toast.error('Failed to save flat rate');
    }
  };

  const handleEditFlatRate = (rate) => {
    setEditingFlatRate(rate);
    setFlatRateData({
      name: rate.name,
      description: rate.description || '',
      price: rate.price,
      min_weight: rate.min_weight,
      max_weight: rate.max_weight,
      weight_unit: rate.weight_unit,
      max_length: rate.max_length,
      max_width: rate.max_width,
      max_height: rate.max_height,
      dimension_unit: rate.dimension_unit,
      package_types: rate.package_types || ['package'],
      delivery_time: rate.delivery_time,
      is_active: rate.is_active,
      order: rate.order || 0
    });
    setShowAddFlatRate(true);
  };

  const handleDeleteFlatRate = async (rateId, rateName) => {
    if (!window.confirm(`Are you sure you want to delete "${rateName}"? This action cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/admin/flat-rates/${rateId}`);
      toast.success('Flat rate deleted successfully');
      loadFlatRates();
    } catch (error) {
      console.error('Failed to delete flat rate:', error);
      toast.error('Failed to delete flat rate');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading settings...</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Shipping & Payment Settings</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Shipping Settings */}
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-4">Shipping Options</h3>
          <div className="space-y-4">
            {/* Shipping Method Selection */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-3">Primary Shipping Method</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="shipping_method_canada_post"
                    name="shipping_method"
                    value="canada_post"
                    defaultChecked={settings?.shipping_settings?.shipping_method === 'canada_post' || !settings?.shipping_settings?.shipping_method}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="shipping_method_canada_post" className="text-sm font-medium">
                    📮 Use Canada Post API (Live quotes with your credentials)
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="shipping_method_flat_rate"
                    name="shipping_method"
                    value="flat_rate"
                    defaultChecked={settings?.shipping_settings?.shipping_method === 'flat_rate'}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="shipping_method_flat_rate" className="text-sm font-medium">
                    📋 Use Flat Rate Shipping (Fixed prices you control)
                  </label>
                </div>
              </div>
              <p className="text-sm text-blue-700 mt-3">
                <strong>Canada Post:</strong> Live quotes using customer address and product data<br/>
                <strong>Flat Rate:</strong> Fixed shipping prices based on product weight/size constraints
              </p>
            </div>
            
            {/* Canada Post API Configuration - Only show when Canada Post is selected */}
            <div className="ml-6 space-y-4 border-l-2 border-green-200 pl-4">
              <h4 className={`font-medium ${theme.textPrimary}`}>Canada Post SOAP API Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Customer Number</label>
                  <input
                    type="text"
                    name="canada_post_customer_number"
                    defaultValue={settings?.shipping_settings?.canada_post_customer_number}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="0008168859"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Contract ID</label>
                  <input
                    type="text"
                    name="canada_post_contract_id"
                    defaultValue={settings?.shipping_settings?.canada_post_contract_id}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="42708755"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">API Key</label>
                  <input
                    type="password"
                    name="canada_post_api_key"
                    defaultValue={settings?.shipping_settings?.canada_post_api_key}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="c94e4186f205bf21ee9a3f"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Environment</label>
                <select
                  name="canada_post_environment"
                  defaultValue={settings?.shipping_settings?.canada_post_environment || `development`}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="development">Development (Testing)</option>
                  <option value="production">Production (Live)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Fuel Surcharge (%)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="25"
                  name="canada_post_fuel_surcharge"
                  defaultValue={settings?.shipping_settings?.canada_post_fuel_surcharge || 0}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="2.5"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>
                  Canada Post fuel surcharge percentage (e.g., 2.5 for 2.5%). This will be added to all shipping quotes.
                </p>
              </div>
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <p className="text-sm text-green-700">
                  <strong>Canada Post SOAP API Setup:</strong><br/>
                  1. Register for Canada Post Developer Program<br/>
                  2. Get your Customer Number (10-digit identifier)<br/>
                  3. Get your Contract ID from Canada Post account<br/>
                  4. Get your API Key for SOAP authentication<br/>
                  5. Use Development for testing, Production for live shipping
                </p>
              </div>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={testCanadaPostConnection}
                  disabled={testingConnection}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                >
                  {testingConnection ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Testing Connection...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Test Canada Post Connection</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Origin Postal Code</label>
                <input
                  type="text"
                  name="origin_postal_code"
                  defaultValue={settings?.shipping_settings?.origin_postal_code}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="K2B 8J6"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Handling Fee ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="handling_fee"
                  defaultValue={settings?.shipping_settings?.handling_fee}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Free Shipping Threshold ($) - Optional</label>
              <input
                type="number"
                step="0.01"
                name="free_shipping_threshold"
                defaultValue={settings?.shipping_settings?.free_shipping_threshold}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="e.g., 50.00"
              />
              <p className={`text-sm ${theme.textSecondary} mt-1`}>Orders above this amount get free shipping</p>
            </div>
          </div>
        </div>

        {/* Flat Rate Shipping Management - Only show when Flat Rate is selected */}
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Flat Rate Shipping Options</h3>
            <button
              type="button"
              onClick={() => setShowAddFlatRate(!showAddFlatRate)}
              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
            >
              {showAddFlatRate ? 'Cancel' : 'Add Flat Rate'}
            </button>
          </div>

          {showAddFlatRate && (
            <div className="bg-purple-50 p-4 rounded-lg mb-4 border border-purple-200">
              <h4 className="text-lg font-semibold mb-4 text-purple-800">
                {editingFlatRate ? 'Edit Flat Rate' : 'Add New Flat Rate'}
              </h4>
              
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Shipping Name</label>
                  <input
                    type="text"
                    value={flatRateData.name}
                    onChange={(e) => setFlatRateData({...flatRateData, name: e.target.value})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="e.g., Standard Shipping, Express Shipping"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={flatRateData.price}
                    onChange={(e) => setFlatRateData({...flatRateData, price: parseFloat(e.target.value) || 0.0})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="15.00"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={flatRateData.description}
                  onChange={(e) => setFlatRateData({...flatRateData, description: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Brief description of this shipping option"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Delivery Time</label>
                  <input
                    type="text"
                    value={flatRateData.delivery_time}
                    onChange={(e) => setFlatRateData({...flatRateData, delivery_time: e.target.value})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="3-7 business days"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Display Order</label>
                  <input
                    type="number"
                    value={flatRateData.order}
                    onChange={(e) => setFlatRateData({...flatRateData, order: parseInt(e.target.value)})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    min="0"
                  />
                </div>
              </div>

              {/* Weight Constraints */}
              <div className="bg-blue-50 p-3 rounded mb-4">
                <h5 className="font-medium mb-2 text-blue-800">Weight Constraints</h5>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1">Min Weight</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={flatRateData.min_weight}
                      onChange={(e) => setFlatRateData({...flatRateData, min_weight: parseFloat(e.target.value) || 0.0})}
                      className={`w-full px-2 py-1 border ${theme.border} rounded text-sm`}
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Max Weight</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={flatRateData.max_weight}
                      onChange={(e) => setFlatRateData({...flatRateData, max_weight: parseFloat(e.target.value) || 999.0})}
                      className={`w-full px-2 py-1 border ${theme.border} rounded text-sm`}
                      placeholder="999.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Unit</label>
                    <select
                      value={flatRateData.weight_unit}
                      onChange={(e) => setFlatRateData({...flatRateData, weight_unit: e.target.value})}
                      className={`w-full px-2 py-1 border ${theme.border} rounded text-sm`}
                    >
                      <option value="lb">pounds (lb)</option>
                      <option value="kg">kilograms (kg)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Dimension Constraints */}
              <div className="bg-green-50 p-3 rounded mb-4">
                <h5 className="font-medium mb-2 text-green-800">Dimension Constraints</h5>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1">Max Length</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={flatRateData.max_length}
                      onChange={(e) => setFlatRateData({...flatRateData, max_length: parseFloat(e.target.value) || 999.0})}
                      className={`w-full px-2 py-1 border ${theme.border} rounded text-sm`}
                      placeholder="999.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Max Width</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={flatRateData.max_width}
                      onChange={(e) => setFlatRateData({...flatRateData, max_width: parseFloat(e.target.value) || 999.0})}
                      className={`w-full px-2 py-1 border ${theme.border} rounded text-sm`}
                      placeholder="999.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Max Height</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={flatRateData.max_height}
                      onChange={(e) => setFlatRateData({...flatRateData, max_height: parseFloat(e.target.value) || 999.0})}
                      className={`w-full px-2 py-1 border ${theme.border} rounded text-sm`}
                      placeholder="999.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Unit</label>
                    <select
                      value={flatRateData.dimension_unit}
                      onChange={(e) => setFlatRateData({...flatRateData, dimension_unit: e.target.value})}
                      className={`w-full px-2 py-1 border ${theme.border} rounded text-sm`}
                    >
                      <option value="inch">inches</option>
                      <option value="cm">cm</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Package Types */}
              <div className="bg-yellow-50 p-3 rounded mb-4">
                <h5 className="font-medium mb-2 text-yellow-800">Applicable Package Types</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {['package', 'envelope', 'oversized', 'document', 'letter', 'postcard', 'tube', 'box'].map(packageType => (
                    <label key={packageType} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={flatRateData.package_types.includes(packageType)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFlatRateData(prev => ({
                              ...prev,
                              package_types: [...prev.package_types, packageType]
                            }));
                          } else {
                            setFlatRateData(prev => ({
                              ...prev,
                              package_types: prev.package_types.filter(type => type !== packageType)
                            }));
                          }
                        }}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm capitalize">{packageType}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-4 mb-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={flatRateData.is_active}
                    onChange={(e) => setFlatRateData({...flatRateData, is_active: e.target.checked})}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium">Active Shipping Option</span>
                </label>
              </div>
              
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={handleSaveFlatRate}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                >
                  {editingFlatRate ? 'Update Flat Rate' : 'Add Flat Rate'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddFlatRate(false);
                    setEditingFlatRate(null);
                    setFlatRateData({
                      name: '', description: '', price: 0.0, min_weight: 0.0, max_weight: 999.0, weight_unit: 'lb',
                      max_length: 999.0, max_width: 999.0, max_height: 999.0, dimension_unit: 'inch',
                      package_types: ['package'], delivery_time: '3-7 business days', is_active: true, order: 0
                    });
                  }}
                  className={`${theme.bgSecondary}0 text-white px-4 py-2 rounded hover:bg-gray-600`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Flat Rate Options Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`${theme.bgSecondary}`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Name</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Price</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Weight Range</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Package Types</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Status</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${theme.card} divide-y divide-gray-200`}>
                {flatRates.map((rate) => (
                  <tr key={rate.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700`}>
                    <td className="px-4 py-4">
                      <div>
                        <div className={`text-sm font-medium ${theme.textPrimary}`}>{rate.name}</div>
                        <div className={`text-xs ${theme.textSecondary}`}>{rate.description}</div>
                        <div className={`text-xs ${theme.textSecondary}`}>{rate.delivery_time}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-bold text-green-600">${rate.price.toFixed(2)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className={`text-sm ${theme.textSecondary}`}>
                        {rate.min_weight} - {rate.max_weight} {rate.weight_unit}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {rate.package_types.slice(0, 3).map(type => (
                          <span key={type} className={`inline-flex px-1 py-0.5 text-xs rounded ${theme.bgSecondary} ${theme.textPrimary}`}>
                            {type}
                          </span>
                        ))}
                        {rate.package_types.length > 3 && (
                          <span className={`text-xs ${theme.textSecondary}`}>+{rate.package_types.length - 3} more</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        rate.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {rate.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm space-x-2">
                      <button
                        type="button"
                        onClick={() => handleEditFlatRate(rate)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteFlatRate(rate.id, rate.name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {flatRates.length === 0 && (
              <div className={`text-center py-8 ${theme.textSecondary}`}>
                No flat rate shipping options found. Add your first flat rate option above.
              </div>
            )}
          </div>
        </div>

        {/* Payment Settings */}
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-4">Payment Options</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="paypal_enabled"
                name="paypal_enabled"
                defaultChecked={settings?.payment_settings?.paypal_enabled}
                className="w-4 h-4"
              />
              <label htmlFor="paypal_enabled">Enable PayPal Payments</label>
            </div>
            
            {/* PayPal Configuration */}
            <div className="ml-6 space-y-4 border-l-2 border-blue-200 pl-4">
              <h4 className={`font-medium ${theme.textPrimary}`}>PayPal Account Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">PayPal Client ID</label>
                  <input
                    type="text"
                    name="paypal_client_id"
                    defaultValue={settings?.payment_settings?.paypal_client_id}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="PayPal Client ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">PayPal Client Secret</label>
                  <input
                    type="password"
                    name="paypal_client_secret"
                    defaultValue={settings?.payment_settings?.paypal_client_secret}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="PayPal Client Secret"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">PayPal Environment</label>
                  <select
                    name="paypal_environment"
                    defaultValue={settings?.payment_settings?.paypal_environment || `sandbox`}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="live">Live (Production)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">PayPal Business Email</label>
                  <input
                    type="email"
                    name="paypal_business_email"
                    defaultValue={settings?.payment_settings?.paypal_business_email}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="business@rg3d.ca"
                  />
                </div>
              </div>
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>PayPal Setup Instructions:</strong><br/>
                  1. Create a PayPal Developer account at developer.paypal.com<br/>
                  2. Create a new app to get Client ID and Client Secret<br/>
                  3. Use Sandbox for testing, Live for production<br/>
                  4. Business email should match your PayPal business account
                </p>
              </div>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={testPayPalConnection}
                  disabled={testingPayPal}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                >
                  {testingPayPal ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Testing PayPal Connection...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Test PayPal Connection</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="etransfer_enabled"
                name="etransfer_enabled"
                defaultChecked={settings?.payment_settings?.etransfer_enabled}
                className="w-4 h-4"
              />
              <label htmlFor="etransfer_enabled">Enable E-transfer Payments</label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">E-transfer Email</label>
                <input
                  type="email"
                  name="etransfer_email"
                  defaultValue={settings?.payment_settings?.etransfer_email}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Currency</label>
                <select
                  name="currency"
                  defaultValue={settings?.payment_settings?.currency}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="USD">USD - US Dollar</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Tax Settings moved to Business Info tab */}
        
        {/* Enhanced Fallback Rate Controls */}
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mt-4">
          <h5 className="font-semibold mb-3 text-orange-800">📊 Fallback Rate Controls (When Canada Post API Unavailable)</h5>
          <p className="text-sm text-orange-700 mb-4">
            Adjust these settings to control fallback shipping rates when Canada Post API is not accessible.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Base Rate ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                name="fallback_base_rate"
                defaultValue={settings?.shipping_settings?.fallback_base_rate || 12.00}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="12.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Weight Multiplier ($/unit)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                name="fallback_weight_multiplier"
                defaultValue={settings?.shipping_settings?.fallback_weight_multiplier || 3.50}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="3.50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Size Multiplier</label>
              <input
                type="number"
                step="0.01"
                min="0"
                name="fallback_size_multiplier"
                defaultValue={settings?.shipping_settings?.fallback_size_multiplier || 0.50}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="0.50"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Regular Parcel Multiplier</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                name="fallback_regular_multiplier"
                defaultValue={settings?.shipping_settings?.fallback_regular_multiplier || 1.0}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="1.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Expedited Multiplier</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                name="fallback_expedited_multiplier"
                defaultValue={settings?.shipping_settings?.fallback_expedited_multiplier || 1.6}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="1.6"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Xpresspost Multiplier</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                name="fallback_xpress_multiplier"
                defaultValue={settings?.shipping_settings?.fallback_xpress_multiplier || 2.1}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="2.1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Priority Multiplier</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                name="fallback_priority_multiplier"
                defaultValue={settings?.shipping_settings?.fallback_priority_multiplier || 3.0}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="3.0"
              />
            </div>
          </div>
          
          <div className="mt-3 p-3 bg-orange-100 rounded border border-orange-300">
            <p className="text-xs text-orange-700">
              <strong>Rate Calculation:</strong> Base Rate + (Weight × Weight Multiplier) + (Size × Size Multiplier) × Service Multiplier<br/>
              <strong>Example:</strong> $12.00 + (0.75lb × $3.50) + (size factor) × 1.6 (Expedited) = Final Rate
            </p>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving Settings...' : 'Save All Settings'}
        </button>
      </form>
    </div>
  );
};

// Category Management Component
export const CategoryManagement = () => {
  const theme = useThemeClasses();
  const [categories, setCategories] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image: '',
    parent_id: null,
    order: 0,
    is_active: true
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      name,
      slug: editingCategory ? formData.slug : generateSlug(name)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const categoryData = {
        ...formData,
        order: parseInt(formData.order)
      };

      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, categoryData);
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/admin/categories`, categoryData);
        toast.success('Category added successfully');
      }

      setFormData({
        name: '',
        slug: '',
        description: '',
        image: '',
        parent_id: null,
        order: 0,
        is_active: true
      });
      setShowAddForm(false);
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      image: category.image || '',
      parent_id: category.parent_id || null,
      order: category.order || 0,
      is_active: category.is_active
    });
    setShowAddForm(true);
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"? This action cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      toast.success('Category deleted successfully');
      loadCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      image: '',
      parent_id: null,
      order: 0,
      is_active: true
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Category Management</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {showAddForm ? 'Cancel' : 'Add Category'}
        </button>
      </div>

      {showAddForm && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-4">
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="e.g., Home & Decor"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">URL Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="e.g., home-decor"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                rows={3}
                placeholder="Brief description of this category..."
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Parent Category (Optional)</label>
                <select
                  value={formData.parent_id || ''}
                  onChange={(e) => setFormData({...formData, parent_id: e.target.value || null})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="">None (Top-level category)</option>
                  {categories.filter(c => !c.parent_id && c.id !== editingCategory?.id).map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Select a parent to make this a sub-category</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  min="0"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category Image URL (Optional)</label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Active Category</label>
            </div>
            
            <div className="flex space-x-4">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
              >
                {editingCategory ? 'Update Category' : 'Add Category'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className={`${theme.bgSecondary}0 text-white px-6 py-2 rounded hover:bg-gray-600`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories Table */}
      <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Categories ({categories.length})</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`${theme.bgSecondary}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Name</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Slug</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Description</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Order</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Status</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${theme.card} divide-y divide-gray-200`}>
                {categories.map((category) => {
                  const isSubCategory = !!category.parent_id;
                  const parentCategory = categories.find(c => c.id === category.parent_id);
                  
                  return (
                  <tr key={category.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700 ${isSubCategory ? 'bg-gray-50 dark:bg-gray-800' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {category.image && (
                          <img
                            className="h-10 w-10 rounded-lg object-cover mr-3"
                            src={getImageUrl(category.image)}
                            alt={category.name}
                          />
                        )}
                        <div>
                          <div className={`text-sm font-medium ${theme.textPrimary} flex items-center`}>
                            {isSubCategory && <span className="mr-2 text-gray-400">├─</span>}
                            {category.name}
                          </div>
                          <div className={`text-sm ${theme.textSecondary}`}>
                            {isSubCategory && parentCategory && (
                              <span className="text-xs text-blue-600">Sub-category of: {parentCategory.name}</span>
                            )}
                            {!isSubCategory && ' '}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className={`text-sm ${theme.bgSecondary} px-2 py-1 rounded`}>{category.slug}</code>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm ${theme.textPrimary} max-w-xs truncate`}>
                        {category.description || `No description`}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                      {category.order}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        category.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {category.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(category.id, category.name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
            {categories.length === 0 && (
              <div className={`text-center py-8 ${theme.textSecondary}`}>
                No categories found. Add your first category to get started.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};